import { type ReactNode } from 'react';
import { Box, Typography, CircularProgress, Fade, type Palette } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { VideocamOff, ErrorOutline, UsbOff, Videocam } from '@mui/icons-material';
import { observer } from 'mobx-lite';
import { MainStoreContextItem } from '../../Stores/MainStore';

interface StatusInfo {
    text: string;
    icon: ReactNode;
    color: string;
    showSpinner?: boolean;
}

const useStatusInfo = (
    loading: boolean,
    permissionGranted: boolean | null,
    devices: MediaDeviceInfo[],
    selectedDeviceId: string | null,
    stream: MediaStream | null,
    error: string | null,
    palette: Pick<Palette, 'grey' | 'error' | 'warning' | 'info'>,
): StatusInfo | null => {
    const iconSx = { fontSize: 40 };

    if (loading && permissionGranted === null)
        return { text: 'Checking camera permissions…', icon: <Videocam sx={{ ...iconSx, color: palette.info.main }} />, color: palette.info.main, showSpinner: true };
    if (permissionGranted === false)
        return { text: 'Camera permission denied. Please enable in browser settings and refresh.', icon: <VideocamOff sx={{ ...iconSx, color: palette.warning.main }} />, color: palette.warning.main };
    if (!loading && permissionGranted === true && devices.length === 0)
        return { text: 'No webcams found. Please connect a webcam.', icon: <UsbOff sx={{ ...iconSx, color: palette.grey[500] }} />, color: palette.grey[500] };
    if (permissionGranted === true && devices.length > 0 && !selectedDeviceId && !stream)
        return { text: 'Select a webcam from the controls below.', icon: <Videocam sx={{ ...iconSx, color: palette.grey[400] }} />, color: palette.grey[400] };
    if (permissionGranted === true && error)
        return { text: error, icon: <ErrorOutline sx={{ ...iconSx, color: palette.error.main }} />, color: palette.error.main };
    return null;
};

export const VideoDisplayPlaceHolder = observer(() => {
    const { mediaDeviceStore } = MainStoreContextItem.useStore();
    const theme = useTheme();
    const { devices, permissionGranted, loading, selectedDeviceId, stream, error } = mediaDeviceStore;
    const status = useStatusInfo(loading, permissionGranted, devices, selectedDeviceId, stream, error, theme.palette);

    return (
        <Box
            sx={{
                aspectRatio: '16/9',
                maxWidth: '100%',
                maxHeight: 'calc(100vh - 300px)',
                minWidth: '280px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[700], 0.5)}`,
                background: `radial-gradient(ellipse at center, ${alpha(theme.palette.grey[900], 0.9)} 0%, ${alpha(theme.palette.common.black, 0.95)} 100%)`,
                px: 4,
                py: 4,
                textAlign: 'center',
                color: theme.palette.grey[500],
            }}
        >
            <Fade in={!!status} timeout={400}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    {status && (
                        <>
                            <Box
                                sx={{
                                    width: 72,
                                    height: 72,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    bgcolor: alpha(status.color, 0.08),
                                    border: `1px solid ${alpha(status.color, 0.15)}`,
                                }}
                            >
                                {status.showSpinner ? <CircularProgress size={32} sx={{ color: status.color }} /> : status.icon}
                            </Box>
                            <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.7), maxWidth: 320, lineHeight: 1.6 }}>
                                {status.text}
                            </Typography>
                        </>
                    )}
                </Box>
            </Fade>
        </Box>
    );
});
